/**
 * **纯展示偏好**的本地持久化（批次 `128`）
 *
 * ## 为什么需要它
 *
 * 「隐藏已结清的分期」「隐藏已清仓的持仓」是**显示开关**，不是业务口径：
 * - 它不该进数据库（不是账本数据，换台设备就该各按各的习惯）；
 * - 但它必须**记住**，否则每次进页面都要重新拨一次。
 *
 * ⇒ 存 `localStorage`，且**失败必须静默**：隐私模式 / 无存储环境下
 * `setItem` 会抛（Safari 隐私模式配额为 0），而"记不住偏好"绝不该让页面崩。
 *
 * ## 🔴 默认值的方向有讲究
 *
 * 两个开关的默认值都是 **`true`（= 隐藏）** —— 与负责人 2026-10-31 的原话一致：
 * 「分期已经结清的部分放在最后并折叠隐藏」「已经清仓的需要隐藏」。
 * ⚠️ 但**开关本身必须一直可见**（页面顶部一行），否则"东西不见了 + 没有入口找回来"
 * 比"列表太长"更糟 —— `AC-INV7` 的原意（清仓不删行）正是为了保证它找得回来。
 *
 * ## 🔴 键名带版本后缀
 *
 * 偏好一旦换了语义（例如以后默认改成"显示"），旧键的 `1` / `0` 会被当成新语义读进来。
 * ⇒ 一律 `.v1` 结尾；改语义就升版本号，旧键自然失效（不需要写迁移）。
 */

/** 读一个布尔偏好（只认 `'1'` / `'0'`，其余一律回退默认值 —— 半截写入不猜） */
export function readUiFlag(key: string, fallback: boolean): boolean {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    if (raw === null || raw === undefined) return fallback;
    if (raw === '1') return true;
    if (raw === '0') return false;
    return fallback;
  } catch {
    // 无存储环境（node 测试基座挂的是只读 stub / 隐私模式）⇒ 用默认值，不阻断渲染
    return fallback;
  }
}

/** 写一个布尔偏好。**失败静默** —— 记不住偏好不是错误，不该冒泡到 UI */
export function writeUiFlag(key: string, value: boolean): void {
  try {
    globalThis.localStorage?.setItem?.(key, value ? '1' : '0');
  } catch {
    // 同上：配额满 / 隐私模式 ⇒ 放弃记忆，本次会话内仍按 state 生效
  }
}

/** 分期页：隐藏已结清（默认隐藏） */
export const INSTALLMENTS_HIDE_SETTLED_KEY = 'jiaying.installments.hideSettled.v1';

/** 投资页：隐藏已清仓（默认隐藏） */
export const INVESTMENTS_HIDE_CLEARED_KEY = 'jiaying.investments.hideCleared.v1';
